import { Stack, useTheme } from "@mui/material";
import { type FC, type ReactNode, useRef, useState } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type { InspirationCta } from "web_myplann/Inspiration/Components/InspirationCtaButton";
import { InspirationDetailFooter } from "web_myplann/Inspiration/Components/InspirationDetailFooter";
import { InspirationDetailHeader } from "web_myplann/Inspiration/Components/InspirationDetailHeader";
import { InspirationDetailHero } from "web_myplann/Inspiration/Components/InspirationDetailHero";
import { InspirationGoalRecapSheet } from "web_myplann/Inspiration/Components/InspirationGoalRecapSheet";
import type { InspirationGoalRecap } from "web_myplann/Inspiration/Types/InspirationTypes";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import { useHistoryEntryScroll } from "web_myplann/Shared/Hooks/useHistoryEntryScroll";

type Props = {
	image: string;
	category: string;
	title: string;
	subtitle: string;
	primaryCta?: InspirationCta;
	secondaryCta?: InspirationCta;
	disclaimer?: string;
	goalRecap?: InspirationGoalRecap;
	scrollResetKey?: string | number | boolean;
	isLoading?: boolean;
	children: ReactNode;
};

export const InspirationDetailLayout: FC<Props> = ({
	image,
	category,
	title,
	subtitle,
	primaryCta,
	secondaryCta,
	disclaimer,
	goalRecap,
	scrollResetKey,
	isLoading = false,
	children,
}) => {
	const { t } = useResource();
	const theme = useTheme();
	const [recapOpen, setRecapOpen] = useState(false);
	const scrollRef = useHistoryEntryScroll("inspirationScroll", !isLoading);

	const pageBg = theme.palette.surface.muted;

	const heroRef = useRef<HTMLDivElement>(null);

	const resolvedPrimaryCta =
		primaryCta && goalRecap
			? { ...primaryCta, onClick: () => setRecapOpen(true) }
			: primaryCta;

	return (
		<MyPlannLayout
			disableContentPadding
			bgColor={pageBg}
			topGlow
			scrollRef={scrollRef}
			scrollResetKey={scrollResetKey}
		>
			<InspirationDetailHeader heroRef={heroRef} title={title} />

			<Stack>
				<InspirationDetailHero
					ref={heroRef}
					image={image}
					category={category}
					title={title}
					subtitle={subtitle}
				/>

				{children}

				{!isLoading && (
					<InspirationDetailFooter
						primaryCta={resolvedPrimaryCta}
						secondaryCta={secondaryCta}
						disclaimer={disclaimer ?? t(Resources.Inspiration.disclaimer)}
					/>
				)}
			</Stack>

			{goalRecap && primaryCta && (
				<InspirationGoalRecapSheet
					open={recapOpen}
					recap={goalRecap}
					onClose={() => setRecapOpen(false)}
					onContinue={() => {
						setRecapOpen(false);
						primaryCta.onClick();
					}}
				/>
			)}
		</MyPlannLayout>
	);
};
